import { CdkConnectedOverlay, CdkOverlayOrigin } from "@angular/cdk/overlay";
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  Host,
  Injector,
  Input,
  Optional,
  Output,
  Renderer2,
  Self,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { NzFormatEmitEvent, NzTreeBaseService, NzTreeHigherOrderServiceToken, NzTreeNode, NzTreeNodeOptions } from "ng-zorro-antd/core/tree";
import { NgStyleInterface } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NzSelectSearchComponent } from "ng-zorro-antd/select";

import { UiTreeSelectService } from "./services/tree-select.service";
import { NzTreeSelectComponent } from "ng-zorro-antd/tree-select";
import { UiSizeLDSType } from "@webapp/ui/core/models/ui-size.models";
import { UiSelectSearchComponent } from "@webapp/ui/select/components/select-search/select-search.component";
import { UiTreeComponent } from "@webapp/ui/tree/tree.component";
import { Directionality } from "@angular/cdk/bidi";
import { FocusMonitor } from "@angular/cdk/a11y";
import { NzNoAnimationDirective } from "ng-zorro-antd/core/no-animation";

export function higherOrderServiceFactory(injector: Injector): NzTreeBaseService {
  return injector.get(UiTreeSelectService);
}

@Component({
  selector: "ui-tree-select",
  exportAs: "uiTreeSelect",
  templateUrl: "tree-select.component.html",
  providers: [
    UiTreeSelectService,
    {
      provide: NzTreeHigherOrderServiceToken,
      useFactory: higherOrderServiceFactory,
      deps: [[new Self(), Injector]],
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiTreeSelectComponent),
      multi: true,
    },
  ],
})
export class UiTreeSelectComponent extends NzTreeSelectComponent {
  @Input("uiId") public nzId: string | null = null;
  @Input("uiAllowClear") @InputBoolean() public nzAllowClear = true;
  @Input("uiShowExpand") @InputBoolean() public nzShowExpand = true;
  @Input("uiShowLine") @InputBoolean() public nzShowLine = false;
  @Input("uiDropdownMatchSelectWidth") @InputBoolean() @WithConfig() public nzDropdownMatchSelectWidth = true;
  @Input("uiCheckable") @InputBoolean() public nzCheckable = false;
  @Input("uiHideUnMatched") @InputBoolean() @WithConfig() public nzHideUnMatched = false;
  @Input("uiShowIcon") @InputBoolean() @WithConfig() public nzShowIcon = false;
  @Input("uiShowSearch") @InputBoolean() public nzShowSearch = false;
  @Input("uiDisabled") @InputBoolean() public nzDisabled = false;
  @Input("uiAsyncData") @InputBoolean() public nzAsyncData = false;
  @Input("uiMultiple") @InputBoolean() public nzMultiple = false;
  @Input("uiDefaultExpandAll") @InputBoolean() public nzDefaultExpandAll = false;
  @Input("uiCheckStrictly") @InputBoolean() public nzCheckStrictly = false;
  @Input("uiVirtualItemSize") public nzVirtualItemSize = 28;
  @Input("uiVirtualMaxBufferPx") public nzVirtualMaxBufferPx = 500;
  @Input("uiVirtualMinBufferPx") public nzVirtualMinBufferPx = 28;
  @Input("uiVirtualHeight") public nzVirtualHeight: string | null = null;
  @Input("uiExpandedIcon") public nzExpandedIcon?: TemplateRef<{ $implicit: NzTreeNode; origin: NzTreeNodeOptions }>;
  @Input("uiNotFoundContent") public nzNotFoundContent?: string;
  @Input("uiNodes") public nzNodes: Array<NzTreeNode | NzTreeNodeOptions> = [];
  @Input("uiOpen") public nzOpen = false;
  @Input("uiSize") @WithConfig() public nzSize: UiSizeLDSType = "default";
  @Input("uiPlaceHolder") public nzPlaceHolder = "";
  @Input("uiDropdownStyle") public nzDropdownStyle: NgStyleInterface | null = null;
  @Input("uiDropdownClassName") public nzDropdownClassName?: string;
  @Input("uiBackdrop") @WithConfig() public nzBackdrop = false;
  @Input("uiExpandedKeys")
  public set nzExpandedKeys(value: string[]) {
    this.expandedKeys = value;
  }
  public get nzExpandedKeys(): string[] {
    return this.expandedKeys;
  }

  @Input("uiDisplayWith") public nzDisplayWith: (node: NzTreeNode) => string | undefined = (node: NzTreeNode) => node.title;
  @Input("uiMaxTagCount") public nzMaxTagCount!: number;
  @Input("uiMaxTagPlaceholder") public nzMaxTagPlaceholder: TemplateRef<{ $implicit: NzTreeNode[] }> | null = null;
  @Output("uiOpenChange") public readonly nzOpenChange = new EventEmitter<boolean>();
  @Output("uiCleared") public readonly nzCleared = new EventEmitter<void>();
  @Output("uiRemoved") public readonly nzRemoved = new EventEmitter<NzTreeNode>();
  @Output("uiExpandChange") public readonly nzExpandChange = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiTreeClick") public readonly nzTreeClick = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiTreeCheckBoxChange") public readonly nzTreeCheckBoxChange = new EventEmitter<NzFormatEmitEvent>();

  @ViewChild(NzSelectSearchComponent, { static: false }) public nzSelectSearchComponent!: UiSelectSearchComponent;
  @ViewChild("treeRef", { static: false }) public treeRef!: UiTreeComponent;
  @ViewChild(CdkOverlayOrigin, { static: true }) public cdkOverlayOrigin!: CdkOverlayOrigin;
  @ViewChild(CdkConnectedOverlay, { static: false }) public cdkConnectedOverlay!: CdkConnectedOverlay;

  @Input("uiTreeTemplate") public nzTreeTemplate!: TemplateRef<{ $implicit: NzTreeNode; origin: NzTreeNodeOptions }>;
  @ContentChild("nzTreeTemplate", { static: true }) public nzTreeTemplateChild!: TemplateRef<{
    $implicit: NzTreeNode;
    origin: NzTreeNodeOptions;
  }>;

  public constructor(
    nzTreeService: UiTreeSelectService,
    nzConfigService: NzConfigService,
    renderer: Renderer2,
    cdr: ChangeDetectorRef,
    elementRef: ElementRef,
    @Optional() directionality: Directionality,
    focusMonitor: FocusMonitor,
    @Host() @Optional() noAnimation?: NzNoAnimationDirective
  ) {
    super(nzTreeService, nzConfigService, renderer, cdr, elementRef, directionality, focusMonitor, noAnimation);
  }
}
